/* =====================================================================================
   Customers: the account database, its intel, and the portal.

   Provenance is the contract here. A field typed by a person is stamped 'operator' in
   field_sources and no Atlas refresh may touch it again. Intel captured from real
   commercial life flows the OTHER way: recorded on the account, and volume intel can be
   applied into Atlas as an attributed, verified row that outranks the registry estimate
   without destroying it.
   ===================================================================================== */

import { BACKEND } from './db';
import { explain } from './dberrors';

const live = () => BACKEND === 'supabase';

export interface Account {
  id: string; name: string; legal_name: string | null; country: string;
  city: string | null; website: string | null; main_phone: string | null;
  status: string; tier: string | null; kinds: string[];
  payment_terms_days: number | null; credit_limit: number | null;
  credit_hold: boolean; invoice_email: string | null; vat_number: string | null;
  company_number: string | null; address: Record<string, string>;
  atlas_site_id: string | null; field_sources: Record<string, string>; notes: string | null;
}
export interface Contact {
  affiliation_id: string; person_id: string; full_name: string;
  role_title: string | null; work_email: string | null; work_phone: string | null;
  notes: string | null;
}
export interface AuditRow {
  id: string; field: string; old_value: string | null; new_value: string | null;
  source: string; changed_by: string | null; created_at: string;
}
export interface SearchHit {
  kind: 'account' | 'person' | 'site'; id: string; label: string;
  sublabel: string | null; account_id: string | null;
}
export interface Intel {
  id: string; account_id: string; metric: string; value: Record<string, unknown>;
  observed_on: string; source: string; note: string | null;
  applied_to_atlas_at: string | null;
}
export interface PortalUser {
  id: string; account_id: string; email: string; full_name: string | null;
  status: string; invited_at: string; last_seen_at: string | null;
}
export interface QuoteRequest {
  id: string; account_id: string; portal_user_id: string;
  lines: { product_id?: string; quantity?: number; note?: string }[];
  message: string | null; status: string; quote_id: string | null; created_at: string;
}

export const INTEL_METRICS: [string, string][] = [
  ['transplant_volume', 'Activity volume (applies to Atlas)'],
  ['test_volume', 'Test volume'],
  ['deployed_platform', 'Deployed platform / incumbent'],
  ['price_paid', 'Price paid'],
  ['budget_cycle', 'Budget cycle'],
  ['decision_maker', 'Decision maker'],
  ['other', 'Other'],
];
export const INTEL_SOURCES: [string, string][] = [
  ['conversation', 'Conversation'], ['order', 'Order'], ['portal', 'Portal'],
  ['visit', 'Site visit'], ['tender', 'Tender'], ['other', 'Other'],
];

async function sb() { const { supabase } = await import('./supabase'); return supabase(); }

export async function fetchAccounts(): Promise<Account[]> {
  if (!live()) return [];
  const { data, error } = await (await sb()).schema('crm').from('accounts')
    .select('id, name, legal_name, country, city, website, main_phone, status, tier, kinds, payment_terms_days, credit_limit, credit_hold, invoice_email, vat_number, company_number, address, atlas_site_id, field_sources, notes')
    .order('name');
  if (error) throw new Error(explain(error.message, 'read the customer database'));
  return (data ?? []) as Account[];
}

export async function createAccount(a: { name: string; country: string; city?: string;
  kinds?: string[] }): Promise<string> {
  const { data, error } = await (await sb()).schema('crm').from('accounts').insert({
    name: a.name, country: a.country, city: a.city || null, kinds: a.kinds ?? [],
    field_sources: { name: 'operator', country: 'operator', city: a.city ? 'operator' : undefined },
  }).select('id').single();
  if (error) throw new Error(explain(error.message, 'create the account'));
  return data.id as string;
}

/* Every operator edit stamps provenance, which is exactly what protects it from Atlas. */
export async function updateAccount(id: string, patch: Partial<Account>, current: Account) {
  const sources = { ...(current.field_sources ?? {}) } as Record<string, string>;
  for (const k of Object.keys(patch)) sources[k] = 'operator';
  const { error } = await (await sb()).schema('crm').from('accounts')
    .update({ ...patch, field_sources: sources }).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the account'));
}

/* ---------------------------------------------------------------- intel */
export async function fetchIntel(accountId: string): Promise<Intel[]> {
  const { data, error } = await (await sb()).schema('crm').from('account_intel')
    .select('*').eq('account_id', accountId).order('observed_on', { ascending: false });
  if (error) throw new Error(explain(error.message, 'read the account intel'));
  return (data ?? []) as Intel[];
}
export async function addIntel(i: { accountId: string; metric: string;
  value: Record<string, unknown>; observedOn?: string; source: string; note?: string }) {
  const { error } = await (await sb()).schema('crm').from('account_intel').insert({
    account_id: i.accountId, metric: i.metric, value: i.value,
    observed_on: i.observedOn || new Date().toISOString().slice(0, 10),
    source: i.source, note: i.note || null,
  });
  if (error) throw new Error(explain(error.message, 'record the intel'));
}
export async function applyIntelToAtlas(intelId: string): Promise<string> {
  const { data, error } = await (await sb()).schema('atlas')
    .rpc('apply_intel', { p_intel: intelId });
  if (error) throw new Error(explain(error.message, 'apply the intel to Atlas'));
  return data as string;
}

/* ---------------------------------------------------------------- portal users */
export async function fetchPortalUsers(accountId?: string): Promise<PortalUser[]> {
  let q = (await sb()).schema('crm').from('portal_users')
    .select('id, account_id, email, full_name, status, invited_at, last_seen_at');
  if (accountId) q = q.eq('account_id', accountId);
  const { data, error } = await q.order('invited_at', { ascending: false });
  if (error) throw new Error(explain(error.message, 'read the portal users'));
  return (data ?? []) as PortalUser[];
}
export async function invitePortalUser(accountId: string, email: string, fullName?: string): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().functions.invoke('portal-invite',
    { body: { accountId, email, fullName } });
  if (error) {
    const ctx = (error as { context?: Response }).context;
    if (ctx) { try { const j = await ctx.json(); if (j?.error) throw new Error(j.error); } catch (e) {
      if ((e as Error).message) throw e; } }
    throw new Error(error.message);
  }
  if (data?.error) throw new Error(data.error);
  return data.portalUserId as string;
}
export async function setPortalUserStatus(id: string, status: 'active' | 'disabled') {
  const { error } = await (await sb()).schema('crm').from('portal_users')
    .update({ status }).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the portal user'));
}

/* ---------------------------------------------------------------- quote requests */
export async function fetchQuoteRequests(): Promise<QuoteRequest[]> {
  if (!live()) return [];
  const { data, error } = await (await sb()).schema('crm').from('portal_quote_requests')
    .select('*').order('created_at', { ascending: false });
  if (error) throw new Error(explain(error.message, 'read the quote requests'));
  return (data ?? []) as QuoteRequest[];
}
export async function setQuoteRequest(id: string, patch: { status?: string; quote_id?: string }) {
  const { error } = await (await sb()).schema('crm').from('portal_quote_requests')
    .update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the quote request'));
}


/* ---------------------------------------------------------------- unified search */
export async function searchCustomers(q: string): Promise<SearchHit[]> {
  const { data, error } = await (await sb()).schema('crm')
    .rpc('search_customers', { p_q: q });
  if (error) throw new Error(explain(error.message, 'search'));
  return (data ?? []) as SearchHit[];
}
export async function placeSuggestions(): Promise<{ city: string; country: string }[]> {
  const { data } = await (await sb()).schema('crm').rpc('place_suggestions');
  return (data ?? []) as { city: string; country: string }[];
}
export async function createAccountFromSite(siteId: string): Promise<string> {
  const { data, error } = await (await sb()).schema('crm')
    .rpc('create_account_from_site', { p_site: siteId });
  if (error) throw new Error(explain(error.message, 'create the customer from Atlas'));
  return data as string;
}
export async function fetchAccountForSite(siteId: string): Promise<{ id: string; name: string; status: string } | null> {
  const { data } = await (await sb()).schema('crm').from('accounts')
    .select('id, name, status').eq('atlas_site_id', siteId).limit(1);
  return (data?.[0] as { id: string; name: string; status: string }) ?? null;
}

/* ---------------------------------------------------------------- contacts */
export async function fetchContacts(accountId: string): Promise<Contact[]> {
  const s = await sb();
  const { data: affs, error } = await s.schema('crm').from('person_affiliations')
    .select('id, person_id, role_title, notes')
    .eq('account_id', accountId).is('ended_on', null);
  if (error) throw new Error(explain(error.message, 'read the contacts'));
  if (!affs?.length) return [];
  const { data: ppl } = await s.schema('crm').from('persons')
    .select('id, full_name, work_email, work_phone')
    .in('id', affs.map((a) => a.person_id));
  return affs.map((a) => {
    const p = (ppl ?? []).find((x) => x.id === a.person_id);
    return { affiliation_id: a.id, person_id: a.person_id,
      full_name: (p?.full_name as string) ?? '', role_title: a.role_title,
      work_email: (p?.work_email as string) ?? null, work_phone: (p?.work_phone as string) ?? null,
      notes: a.notes };
  });
}
export async function addContact(c: { accountId: string; firstName?: string; lastName: string;
  roleTitle?: string; email?: string; phone?: string; notes?: string }) {
  const s = await sb();
  const { data: person, error } = await s.schema('crm').from('persons').insert({
    first_name: c.firstName || null, last_name: c.lastName,
    work_email: c.email || null, work_phone: c.phone || null,
  }).select('id').single();
  if (error) throw new Error(explain(error.message, 'create the contact'));
  const { error: e2 } = await s.schema('crm').from('person_affiliations').insert({
    person_id: person.id, account_id: c.accountId,
    role_title: c.roleTitle || null, notes: c.notes || null, is_primary: false,
  });
  if (e2) throw new Error(explain(e2.message, 'attach the contact'));
}
export async function removeContact(affiliationId: string) {
  const { error } = await (await sb()).schema('crm').from('person_affiliations')
    .delete().eq('id', affiliationId);
  if (error) throw new Error(explain(error.message, 'remove the contact'));
}

/* ---------------------------------------------------------------- audit + distributor */
export async function fetchAudit(accountId: string): Promise<AuditRow[]> {
  const { data, error } = await (await sb()).schema('crm').from('account_audit')
    .select('id, field, old_value, new_value, source, changed_by, created_at')
    .eq('account_id', accountId).order('created_at', { ascending: false }).limit(50);
  if (error) throw new Error(explain(error.message, 'read the history'));
  return (data ?? []) as AuditRow[];
}
export async function setDistributor(accountId: string, distributorId: string | null) {
  const { error } = await (await sb()).schema('crm')
    .rpc('set_account_distributor', { p_account: accountId, p_distributor: distributorId });
  if (error) throw new Error(explain(error.message, 'set the distributor'));
}
export async function fetchDistributor(accountId: string): Promise<string | null> {
  const { data } = await (await sb()).schema('crm').from('account_relationships')
    .select('from_account').eq('to_account', accountId).eq('relationship', 'sells_to').limit(1);
  return (data?.[0]?.from_account as string) ?? null;
}
